import type { TicketType } from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import type { Event } from '~/shared/types'

const TICKET_TIME_ZONE = 'Europe/Vienna'
export const PREVIEW_QR_PAYLOAD = 'PULSIFY-TICKET-PREVIEW'
const PREVIEW_REFERENCE = 'PUL-3F9A1C07'

export type TicketFaceData = {
  eventTitle: string
  typeName: string
  holderName: string
  venue: string
  dateLabel: string
  day: string
  coverUrl: string | null
  doorPriceCents: number | null
  isStudent: boolean
  reference: string
}

export function ticketDateLabel(iso: string, locale: string = getLocale()): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone: TICKET_TIME_ZONE,
    day: 'numeric',
    month: 'short',
  })
    .format(new Date(iso))
    .replace(/\.$/, '')
}

export function ticketDay(iso: string): string {
  return new Intl.DateTimeFormat('en', { timeZone: TICKET_TIME_ZONE, day: 'numeric' }).format(
    new Date(iso),
  )
}

export function ticketVenue(location: { name?: string | null; city?: string | null }): string {
  const name = location.name?.trim() ?? ''
  const city = location.city?.trim() ?? ''
  if (!name) return city
  if (!city || name.toLowerCase().includes(city.toLowerCase())) return name
  return `${name} ${city}`
}

export function formatEurosShort(cents: number, locale: string = getLocale()): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)
}

export function previewTicket(
  event: Pick<Event, 'title' | 'startTime' | 'coverImageUrl' | 'location'>,
  type: Pick<TicketType, 'name' | 'priceCents' | 'doorPriceCents' | 'isStudent'> | null,
  locale: string = getLocale(),
): TicketFaceData {
  return {
    eventTitle: event.title,
    typeName: type?.name ?? m.ticketPreviewDefaultType({}, { locale: asLocale(locale) }),
    holderName: m.ticketPreviewSampleName({}, { locale: asLocale(locale) }),
    venue: ticketVenue(event.location),
    dateLabel: ticketDateLabel(event.startTime, locale),
    day: ticketDay(event.startTime),
    coverUrl: event.coverImageUrl || null,
    doorPriceCents: type && type.priceCents === 0 ? type.doorPriceCents : null,
    isStudent: type?.isStudent ?? false,
    reference: PREVIEW_REFERENCE,
  }
}

function asLocale(locale: string): 'de' | 'en' {
  return locale.startsWith('de') ? 'de' : 'en'
}
